/**
 * Subscription Context
 * Provides subscription state and quota checking functions throughout the app
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { trackSubscriptionUpgrade } from '../services/analytics';
import api from '../services/api';

const SubscriptionContext = createContext();

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
}

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [launchPromoActive, setLaunchPromoActive] = useState(false);

  // Fetch launch promo status on mount (public endpoint, no auth needed)
  useEffect(() => {
    const fetchPromoStatus = async () => {
      try {
        const response = await api.get('/subscription/tiers');
        setLaunchPromoActive(response.data.launchPromoActive || false);
      } catch (err) {
        console.error('Error fetching promo status:', err);
      }
    };
    fetchPromoStatus();
  }, []);

  // Fetch subscription when user changes
  useEffect(() => {
    if (user) {
      fetchSubscription();
    } else {
      setSubscription(null);
      setLoading(false);
    }
  }, [user]);

  /**
   * Fetch user's subscription from API
   */
  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/subscription');

      setSubscription(response.data.subscription);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.response?.data?.message || 'Failed to load subscription');

      // Set default free tier on error to prevent blocking UI
      setSubscription({
        tier: 'free',
        status: 'active',
        limits: { maxMonitors: 5, maxAutoBookings: 0, priority: 'low' },
        usage: { activeMonitors: 0, autoBookingsUsed: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Upgrade to a higher tier
   */
  const upgradeTier = async (newTier) => {
    try {
      const response = await api.post('/subscription/upgrade', { tier: newTier });

      setSubscription(response.data.subscription);
      trackSubscriptionUpgrade(newTier);
      return { success: true, message: response.data.message };
    } catch (err) {
      console.error('Error upgrading tier:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to upgrade'
      };
    }
  };

  /**
   * Downgrade to a lower tier
   */
  const downgradeTier = async (newTier) => {
    try {
      const response = await api.post('/subscription/downgrade', { tier: newTier });

      setSubscription(response.data.subscription);
      return { success: true, message: response.data.message };
    } catch (err) {
      console.error('Error downgrading tier:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to downgrade'
      };
    }
  };

  /**
   * Cancel subscription
   */
  const cancelSubscription = async (immediate = false) => {
    try {
      const response = await api.post('/subscription/cancel', { immediate });

      await fetchSubscription(); // Refresh subscription data
      return { success: true, message: response.data.message };
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to cancel'
      };
    }
  };

  /**
   * Create Stripe checkout session for upgrading to a paid tier
   * @param {string} tier - Target tier (basic, pro)
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  const createCheckoutSession = async (tier) => {
    try {
      const response = await api.post('/subscription/create-checkout-session', { tier });

      return { success: true, url: response.data.url };
    } catch (err) {
      console.error('Error creating checkout session:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to create checkout session'
      };
    }
  };

  /**
   * Create Stripe Customer Portal session for managing billing
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  const createPortalSession = async () => {
    try {
      const response = await api.post('/subscription/create-portal-session');

      return { success: true, url: response.data.url };
    } catch (err) {
      console.error('Error creating portal session:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to open billing portal'
      };
    }
  };

  /**
   * Redeem a promo code to upgrade tier for free
   */
  const redeemPromoCode = async (tier, promoCode) => {
    try {
      const response = await api.post('/subscription/redeem-promo', { tier, promoCode });

      await fetchSubscription();
      return { success: true, message: response.data.message };
    } catch (err) {
      console.error('Error redeeming promo code:', err);
      return {
        success: false,
        error: err.response?.data?.message || 'Failed to redeem promo code'
      };
    }
  };

  /**
   * Check if user can create a monitor (client-side check)
   */
  const canCreateMonitor = () => {
    if (!subscription) return false;

    const { activeMonitors } = subscription.usage;
    const { maxMonitors } = subscription.limits;

    return activeMonitors < maxMonitors;
  };

  /**
   * Check if user can use auto-booking
   */
  const canUseAutoBooking = () => {
    if (!subscription) return false;

    const { maxAutoBookings } = subscription.limits;
    return maxAutoBookings > 0;
  };

  /**
   * Check if user has auto-booking quota remaining
   */
  const hasAutoBookingQuota = () => {
    if (!subscription) return false;

    const { autoBookingsUsed } = subscription.usage;
    const { maxAutoBookings } = subscription.limits;

    return autoBookingsUsed < maxAutoBookings;
  };

  /**
   * Get remaining monitor slots
   */
  const getRemainingMonitors = () => {
    if (!subscription) return 0;

    const { activeMonitors } = subscription.usage;
    const { maxMonitors } = subscription.limits;

    return Math.max(0, maxMonitors - activeMonitors);
  };

  /**
   * Get remaining auto-booking quota
   */
  const getRemainingAutoBookings = () => {
    if (!subscription) return 0;

    const { autoBookingsUsed } = subscription.usage;
    const { maxAutoBookings } = subscription.limits;

    return Math.max(0, maxAutoBookings - autoBookingsUsed);
  };

  /**
   * Check if user is on paid tier
   */
  const isPaidTier = () => {
    if (!subscription) return false;
    return subscription.tier === 'basic' || subscription.tier === 'pro';
  };

  /**
   * Get tier display name
   */
  const getTierName = () => {
    if (!subscription) return 'Free';

    const tierNames = {
      free: 'Free',
      basic: 'Basic',
      pro: 'Pro'
    };

    return tierNames[subscription.tier] || 'Free';
  };

  /**
   * Get tier color for badges
   */
  const getTierColor = () => {
    if (!subscription) return '#6b7280'; // gray

    const colors = {
      free: '#6b7280',    // gray
      basic: '#3b82f6',   // blue
      pro: '#f59e0b'      // gold
    };

    return colors[subscription.tier] || '#6b7280';
  };

  const value = {
    subscription,
    loading,
    error,
    launchPromoActive,
    fetchSubscription,
    upgradeTier,
    downgradeTier,
    cancelSubscription,
    createCheckoutSession,
    createPortalSession,
    redeemPromoCode,
    canCreateMonitor,
    canUseAutoBooking,
    hasAutoBookingQuota,
    getRemainingMonitors,
    getRemainingAutoBookings,
    isPaidTier,
    getTierName,
    getTierColor
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
